import { useEffect } from 'react';

/**
 * Lightweight SEO helper.
 * Dynamically updates <title>, <meta description>, <meta keywords>,
 * canonical URL, and Open Graph / Twitter meta tags per route.
 * Ensures every page has a unique title and description.
 */
const setMeta = (name, content, attr = 'name') => {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const setLink = (rel, href) => {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

const Seo = ({
  title,
  description,
  keywords,
  image,
  url,
  type = 'website',
  jsonLd,
}) => {
  useEffect(() => {
    const canonical = url || (typeof window !== 'undefined' ? window.location.href : '');

    if (title) document.title = title;
    setMeta('description', description);
    setMeta('keywords', keywords);

    // Open Graph
    setMeta('og:title', title, 'property');
    setMeta('og:description', description, 'property');
    setMeta('og:type', type, 'property');
    setMeta('og:url', canonical, 'property');
    if (image) setMeta('og:image', image, 'property');

    // Twitter
    setMeta('twitter:card', image ? 'summary_large_image' : 'summary');
    setMeta('twitter:title', title);
    setMeta('twitter:description', description);
    if (image) setMeta('twitter:image', image);

    // Canonical
    setLink('canonical', canonical);

    // JSON-LD structured data
    let ldEl = document.head.querySelector('script[data-seo-jsonld="true"]');
    if (jsonLd) {
      if (!ldEl) {
        ldEl = document.createElement('script');
        ldEl.type = 'application/ld+json';
        ldEl.setAttribute('data-seo-jsonld', 'true');
        document.head.appendChild(ldEl);
      }
      ldEl.textContent = JSON.stringify(jsonLd);
    } else if (ldEl) {
      ldEl.remove();
    }
  }, [title, description, keywords, image, url, type, jsonLd]);

  return null;
};

export default Seo;
